import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { SKIP_NOTIFICATION } from '~core/interceptors/error-handler.interceptor';

import { environment } from '../../../environments/environment';

/**
 * Reads and updates the company logo that lives on the Odoo partner
 * (res.partner.image_1920), exposed via the Odoo FastAPI at
 * `{odooUrl}accounts/{accountId}/logo`. Mirrors {@link OrderLineService} in
 * gating on a configured Odoo URL (empty on dev/staging).
 */
@Injectable({ providedIn: 'root' })
export class CompanyService {
	private odooUrl: string = environment.odooUrl;

	constructor(private readonly http: HttpClient) {}

	/** True when an Odoo backend URL is configured, i.e. the logo can be fetched/updated. */
	public get isAvailable(): boolean {
		return this.odooUrl.length > 0;
	}

	/**
	 * Fetches the logo as its real binary (served with the correct Content-Type,
	 * whatever the raster format) and wraps it in an object URL for an `<img>`.
	 * Returns null when there is no logo or the backend is unreachable — the
	 * caller then renders the placeholder. The caller owns the returned object
	 * URL and must revoke it when replacing it or on destroy.
	 */
	public getLogo(accountId: string): Observable<string | null> {
		if (!this.odooUrl.length) {
			return of(null);
		}

		const options = {
			responseType: 'blob' as const,
			context: new HttpContext().set(SKIP_NOTIFICATION, true),
		};
		return this.http.get(this.logoUrl(accountId), options).pipe(
			map((blob) => URL.createObjectURL(blob)),
			catchError(() => of(null))
		);
	}

	/**
	 * Uploads a new logo (204 on success). Errors propagate so the caller can
	 * surface the backend's `detail`; the default (JSON) response type keeps that
	 * error body parseable. The caller refetches via {@link getLogo} to display
	 * the stored, re-encoded result.
	 */
	public updateLogo(accountId: string, file: File): Observable<void> {
		const formData = new FormData();
		formData.append('file', file);

		const options = { context: new HttpContext().set(SKIP_NOTIFICATION, true) };
		return this.http.put<void>(this.logoUrl(accountId), formData, options);
	}

	private logoUrl(accountId: string): string {
		return new URL(`accounts/${accountId}/logo`, this.odooUrl).href;
	}
}
